import type { ApiCallResponse } from './fetching'
import { apiCall } from './fetching'

// Extracted from ./klarna-utils so that the pure order-line factories there
// (klarnaOrderLineFactory, …) can be imported by client checkout components
// without pulling in ./fetching (and its server-only credentials store).

interface KlarnaEndpointOptions<T> {
  orderId?: string
  payload?: T
}

type KlarnaApiCallResponse<T> = T | { error_code: string }

export const klarnaApiCall = async <T, K>(
  type: 'create' | 'get' | 'update' | 'acknowledge' | 'cancel',
  options: KlarnaEndpointOptions<K>
): Promise<ApiCallResponse<T>> => {
  const { orderId, payload } = options

  const klarnaEndpointDictionary = {
    create: {
      method: 'POST',
      url: `${process.env.KLARNA_URL}/checkout/v3/orders`
    },

    update: {
      method: 'POST',
      url: `${process.env.KLARNA_URL}/checkout/v3/orders/${orderId}`
    },

    get: {
      method: 'GET',
      url: `${process.env.KLARNA_URL}/checkout/v3/orders/${orderId}`
    },

    acknowledge: {
      method: 'POST',
      url: `${process.env.KLARNA_URL}/ordermanagement/v1/orders/${orderId}/acknowledge`
    },

    cancel: {
      method: 'POST',
      url: `${process.env.KLARNA_URL}/ordermanagement/v1/orders/${orderId}/cancel`
    }
  }

  const credentials = Buffer.from(
    `${process.env.KLARNA_USERNAME}:${process.env.KLARNA_PASSWORD}`
  ).toString('base64')
  const headers = { Authorization: `Basic ${credentials}`, 'Content-Type': 'application/json' }
  const props = {
    headers,
    method: klarnaEndpointDictionary[type].method,
    // optional body if payload
    ...(payload ? { body: JSON.stringify(payload) } : {})
  }

  const { error, data } = await apiCall<KlarnaApiCallResponse<T>>(
    klarnaEndpointDictionary[type].url,
    props
  )

  if (error || !data) {
    return {
      data: null,
      error
    }
  }

  // @ts-expect-error -- FIX
  if ('error_code' in data) {
    return {
      data: null,
      error: data.error_code
    }
  }

  return {
    data,
    error: null
  }
}
